<template>
  <div
    class="pane-footer pa-6 position-relative row-gap-2 d-flex flex-column"
    data-locator="ai-assistant-footer"
  >
    <template v-if="hasCreditsRemaining">
      <AIAssistantRemainingCredits
        :credits="credits"
        :credits-label="`${TRANSLATION_KEY_PREFIX}.remainingCreditsLabel`"
        :credits-pending="creditsPending"
        :credits-threshold-warning="5"
      />
      <AIAssistantPaneForm
        :cleared="!postPromptStreaming"
        :disabled="postPromptStreaming || postPromptPending"
        :question-type="questionType"
        :selected-documents="askDocDocuments"
        @update:selectedDocuments="askDocDocuments = $event"
        @post-prompt="onPostPrompt"
        @draft-content-changed="onDraftContentChanged"
        @sources-confirmed="$emit('sources-confirmed', $event)"
      />
    </template>
    <app-text-divider v-else-if="!hasCreditsRemaining" variant="dashed" color="#5F39AC">
      <div class="remaining-credits-alert d-flex column-gap-3 align-center">
        <app-material-icon
          name="warning"
          filled
          color="#7247C9"
          :size="30"
        />
        <p class="text-center text--x-small">
          <span class="text-purple-darken-1 font-weight-bold">{{ $t(`${TRANSLATION_KEY_PREFIX}.remainingCreditsAlertHighlight`, credits ? credits.total : 0, { count: credits ? credits.total : 0 }) }}</span> <br />
          {{ $t(`${TRANSLATION_KEY_PREFIX}.remainingCreditsAlertMessage`) }}
        </p>
      </div>
    </app-text-divider>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { mapState, mapMutations } from 'vuex'

import { AIAssistantMutationTypes } from '@/store/modules/ai-assistant/mutation_types'
import { type AIAssistantSelectedDocument } from '@/types/ai-assistant'

import AIAssistantPaneForm from './AIAssistantPaneForm.vue'
import AIAssistantRemainingCredits from './AIAssistantRemainingCredits.vue'

const TRANSLATION_KEY_PREFIX = 'common.ai-assistant.AIAssistantPaneFooter'

export default defineComponent({
  name: 'AIAssistantPaneFooter',
  emits: ['post-prompt', 'draft-content-changed', 'sources-confirmed'],
  components: {
    AIAssistantPaneForm,
    AIAssistantRemainingCredits,
  },
  data() {
    return {
      TRANSLATION_KEY_PREFIX,
    }
  },
  computed: {
    ...mapState('aiAssistant', [
      'credits',
      'creditsPending',
      'postPromptPending',
      'postPromptStreaming',
      'questionType',
      'selectedDocuments',
    ]),
    hasCreditsRemaining(): boolean {
      return this.credits.remaining > 0
    },
    askDocDocuments: {
      get(): AIAssistantSelectedDocument[] {
        return this.selectedDocuments
      },
      set(value: AIAssistantSelectedDocument[]) {
        this.SET_SELECTED_DOCUMENTS(value || [])
      },
    },
  },
  methods: {
    ...mapMutations('aiAssistant', [AIAssistantMutationTypes.SET_SELECTED_DOCUMENTS]),
    onPostPrompt(prompt: string) {
      this.$emit('post-prompt', prompt)
    },
    onDraftContentChanged(hasDraft: boolean) {
      this.$emit('draft-content-changed', hasDraft)
    },
  },
})
</script>

<style lang="scss" scoped>
.pane-footer {
  .remaining-credits {
    color: #2A476A;
  }
  .remaining-credits-alert {
    margin: 0 auto;
    width: fit-content;
    padding: 0 16px;
  }
}
</style>
